import IdeaProperty, {defaultFormulaOf, defaultValueOf} from "@/types/IdeaProperty"
import {booleanFace, formatSimple} from "@/utils/propertyDisplay"
import {ACCESS_LABELS, PROPERTY_ACCESS_KINDS, PropertyAccess, PropertyAccessKind} from "@/types/Idea"
import Domain, {DomainOption} from "@/types/Domain"
import {asArray} from "@/utils/arrayUtils"
import {formulaName, isNullOrUndefined} from "@/utils/utils"
import types from "@/model/types"
import {writeConditions} from "@/model/conditions"
import {MessageKey, t} from "@/i18n"
import {isMediaType} from "@/utils/mediaTypes"

/**
 * Descreve a DEFINIÇÃO de uma propriedade, em linhas rotuladas.
 *
 * É o par de utils/propertyDisplay, que descreve o VALOR. Antes isto era o
 * propertyData() do ViewClass, que achatava tudo numa string separada por vírgulas
 * e mencionava 6 dos ~13 campos — alias, subtipo, cardinalidade, identificador e
 * script simplesmente não apareciam.
 *
 * Regra: toda característica vira uma linha, inclusive quando a resposta é "Não".
 * Nada fica subentendido pela ausência.
 *
 * Puro de propósito: não conhece Vue nem o workspace. Os nomes de coleção e de
 * propriedade chegam por um resolvedor, que por padrão devolve o próprio alias.
 */

/**
 * Uma marca de sim/não — um selo, na cor do que ela quer dizer (pedido do usuário: no cinza de todas,
 * elas se confundiam). `condition`: a condição escrita, quando ela vale só às vezes — e aí o rótulo diz
 * "se…".
 */
export type PropertyMark = { icon: string, label: string, color: string, condition: string }

/**
 * A definição de uma propriedade na forma de cada conteúdo (pedido do usuário: um visual que tenha a
 * ver com o conteúdo, e não pares de nome e valor): o que o campo é numa frase, os nomes técnicos em
 * código, as marcas em selos, a fórmula e as regras como código.
 */
export type PropertyFace = {
    icon: string
    name: string
    /** O identificador (URL), em código. */
    alias: string
    /** O nome em fórmulas, só quando difere do identificador — quase sempre é o mesmo. */
    formulaName: string
    /** O que o campo é: o tipo ou o destino, com que valor começa e quantos valores. */
    description: string
    /** As opções da seleção, com a cor de cada uma — desenhadas como o valor dela no item. */
    options: DomainOption[]
    marks: PropertyMark[]
    formula: string
    /** Cada regra como foi escrita, e a mensagem dela — a própria, ou a padrão. */
    rules: Array<{ rule: string, message: string }>
    /** Quem vê e quem edita o campo, cada fórmula com a pergunta dela ("Quem vê"). */
    access: Array<{ kind: PropertyAccessKind, formula: string, label: string }>
}

/**
 * Resolve alias em objeto. `property` devolve a propriedade inteira, e não só o
 * nome, porque o espelho de um relacionamento não guarda a própria cardinalidade:
 * ela é derivada do outro lado.
 */
export type NameResolver = {
    idea: (alias: string) => string
    property: (ideaAlias: string, propertyAlias: string) => IdeaProperty | undefined
    /** O domínio de um atributo de seleção: dele saem o nome, as opções e o valor default. */
    domain: (domainAlias: string | undefined) => Domain | undefined
}

const ALIAS_ONLY: NameResolver = {
    idea: alias => alias,
    property: () => undefined,
    domain: () => undefined
}

/**
 * O valor padrão escrito por extenso: os NOMES das opções, nunca os ids; o sim/não como a ficha o
 * mostra; a data no formato de quem lê; e a fórmula, com o `=` na frente, quando ele é por fórmula.
 *
 * O id é interno e não diz nada a quem lê um diagrama ou uma ficha de coleção. Opção que não
 * existe mais sai do texto — é a mesma regra de quem semeia, que ignora id pendurado, e pelo
 * mesmo motivo: mostrar um número cru seria mostrar defeito de dado no lugar do valor. O item e a
 * pessoa só têm o id aqui, e ficam de fora pelo mesmo motivo.
 *
 * Vazio quando não há padrão, que é o estado normal — e é o que faz a linha não aparecer nem na
 * ficha nem na caixa.
 */
export function defaultLabel(property: IdeaProperty, names: NameResolver = ALIAS_ONLY): string {
    const formula = defaultFormulaOf(property)
    if (formula) return `= ${formula}`
    const value = defaultValueOf(property)
    if (isNullOrUndefined(value) || property.relationship || property.type === 'user') return ''
    if (property.type === 'boolean') return booleanFace(!!value).label
    if (property.type !== 'choice') return asArray(value).map(one => formatSimple(property, one)).join(', ')
    const options = names.domain(property.domain)?.options || []
    return asArray(value)
        .map(id => options.find(option => option.id === id))
        .filter(option => !!option)
        .map(option => option!.name)
        .join(', ')
}

/** As marcas de sim/não de uma propriedade. */
export type MarkKind = 'required' | 'unique' | 'multiple' | 'computed' | 'identifier'

/**
 * O ícone e a cor de cada marca, num dono só: os selos da lista de propriedades e os pares da janela
 * de propriedade (o lado ligado) leem daqui. Uma cor por sentido (escolha do usuário): o vermelho do *
 * dos campos obrigatórios; o roxo da impressão digital, que só um item tem; o verde-azulado da fórmula;
 * o azul da chave do identificador. Quem vê e quem edita não são marcas: são fórmulas, e a ficha as
 * mostra como mostra a calculada e as regras.
 */
export const MARKS: Record<MarkKind, { icon: string, color: string }> = {
    required: {icon: 'mdi-asterisk', color: 'error'},
    unique: {icon: 'mdi-fingerprint', color: 'deep-purple'},
    multiple: {icon: 'mdi-format-list-bulleted', color: 'blue-grey'},
    computed: {icon: 'mdi-function-variant', color: 'teal'},
    identifier: {icon: 'mdi-key', color: 'primary'}
}

/**
 * Marca tri-estado. Falso não vira selo: a ausência já diz. Verdadeiro vira o rótulo sozinho —
 * "Obrigatório" basta, "Obrigatório: Sim" é redundante. Só a condição precisa ser dita.
 */
function markOf(kind: MarkKind, label: string, value: any): PropertyMark | null {
    if (!value) return null
    const {icon, color} = MARKS[kind]
    if (value === true) return {icon, label, color, condition: ''}
    return {icon, label: t('definition.mark-if', {mark: label}), color,
        condition: t('definition.if', {conditions: writeConditions(value)})}
}

/**
 * Rótulo do tipo, com a configuração dele entre parênteses. Exportado: o diagrama usa o mesmo.
 *
 * Em seleção o parêntese é o DOMÍNIO, pela mesma razão que nos demais é o subtipo: é a
 * configuração que o tipo pede, e sem ela "Seleção" não diz o que o campo aceita. É também o que
 * o formulário do atributo já faz ao pôr o domínio no lugar do subtipo.
 */
export function typeLabel(property: IdeaProperty, names: NameResolver = ALIAS_ONLY): string {
    if (!property.type) return t('definition.none')
    const type = types[property.type]
    const name = type ? type.name : property.type
    const domain = property.type === 'choice' ? names.domain(property.domain) : undefined
    if (domain) return `${name} (${domain.name})`
    const subtype = type && type.subtypes && property.subtype ? type.subtypes[property.subtype] : undefined
    return subtype ? `${name} (${subtype.name})` : name
}

/**
 * O valor desta propriedade viaja como LISTA?
 *
 * **Reverso conta como múltiplo mesmo sem `multiple` declarado**: o servidor sempre o serializa
 * como array, porque o valor dele mora nos documentos do outro lado e chega como a coleção
 * deles. É por isso que esta pergunta não é a mesma do `effectiveCardinality`, que responde
 * "este item pode apontar para vários?" — no espelho aquela sai do `unique` do outro lado, e vem
 * FALSA num espelho de campo não-exclusivo, enquanto o campo continua sendo uma lista.
 *
 * Estava escrita em três lugares — o portão do Kanban de relacionamento, o valor que vai ao
 * servidor e a dimensão do quadro —, e a terceira cópia nasceu errada justamente por confundir
 * as duas perguntas: a dimensão não se declarava múltipla, o arrasto não perguntava nada e a
 * gravação mandava um id solto onde o servidor espera a lista inteira.
 */
export function travelsAsList(property: IdeaProperty): boolean {
    return !!property.multiple || !!(property.relationship && property.relationship.reverse)
}

/** Por que um campo não pode compor o identificador — ver `identifierBlocker`. */
export type IdentifierBlock = 'multiple' | 'computed' | 'user' | 'media' | 'not-required' | 'required-condition'
    | 'visibility' | 'formula-reads'

/**
 * Por que este campo NÃO pode compor o identificador — o nome dos itens —, ou null se pode.
 *
 * O nome é um texto só, de um valor por campo, que todo item tem: nada de lista, de relacionamento
 * calculado, de pessoa (o nome dela muda com a conta), de arquivo (imagem ou áudio: o valor é um
 * endereço — pedido do usuário) nem de campo que pode ficar vazio, sempre ou às vezes. Nem o que tem
 * fórmula de visibilidade: o nome aparece em todo lugar, e mostraria o campo a quem não o vê (decisão do
 * usuário).
 *
 * O atributo calculado pode (pedido do usuário), sem ser obrigatório — ninguém o preenche —, desde
 * que a fórmula não leia o que o nome não alcança: o `nameBlocker` que o servidor publica. No rascunho
 * do editor ainda não há; o servidor confere ao salvar.
 *
 * Um dono para os três que perguntam: o editor de atributo, o de relacionamento e a caixa do
 * identificador no design da coleção — e o servidor confere de novo, pelo par dela
 * (`IdeaProperty.identifierBlocker`). Recebe a propriedade gravada (`formula`) ou o rascunho do
 * editor (`computed`).
 */
export function identifierBlocker(
    property: { type?: string, multiple?: boolean, required?: any, formula?: string, computed?: boolean,
        relationship?: unknown, access?: PropertyAccess, nameBlocker?: string }
): IdentifierBlock | null {
    const computed = !!(property.computed || property.formula)
    if (property.multiple) return 'multiple'
    if (computed && property.relationship) return 'computed'
    if (property.type === 'user') return 'user'
    if (isMediaType(property)) return 'media'
    if (computed && property.nameBlocker) return 'formula-reads'
    if (!computed && property.required !== true) return property.required ? 'required-condition' : 'not-required'
    if (property.access && property.access.visible !== undefined) return 'visibility'
    return null
}

const IDENTIFIER_BLOCK_TEXTS: Record<IdentifierBlock, MessageKey> = {
    'multiple': 'identifier-blocked.multiple',
    'computed': 'identifier-blocked.computed',
    'user': 'identifier-blocked.user',
    'media': 'identifier-blocked.media',
    'not-required': 'identifier-blocked.not-required',
    'required-condition': 'identifier-blocked.required-condition',
    'visibility': 'identifier-blocked.visibility',
    'formula-reads': 'identifier-blocked.formula-reads'
}

/**
 * "«Itens» não pode compor o nome dos itens: tem mais de um valor." Um dono para os dois que dizem:
 * a caixa do identificador enquanto se arrasta, e a recusa do servidor (`saveErrorMessage`). `read` é,
 * no `formula-reads`, o que a fórmula lê ("a fórmula lê currentUser").
 */
export function identifierRefusalText(propertyName: string, blocker: IdentifierBlock, read?: string): string {
    return t('identifier-refused', {property: propertyName, reason: t(IDENTIFIER_BLOCK_TEXTS[blocker], {name: read})})
}

/** Ícone da espécie/tipo da propriedade. Exportado: a ficha usa o mesmo nas seções. */
export function typeIcon(property: IdeaProperty): string {
    if (property.relationship) {
        return property.relationship.reverse ? 'mdi-arrow-left-right' : 'mdi-arrow-right'
    }
    const type = property.type ? types[property.type] : undefined
    return type ? type.icon : 'mdi-help'
}

function cardinalityValue(property: IdeaProperty, multiple: boolean): string {
    if (!multiple) return t('definition.single-value')
    const count = property.itemCount
    const min = count && count.min ? count.min : 0
    const max = count && count.max ? count.max : 0
    if (!min && !max) return t('definition.many-values')
    return t('definition.many-values-range', {
        min: min || t('definition.none'),
        max: max || t('unlimited')
    })
}

/** A propriedade do outro lado, quando esta é um espelho. */
function mirroredProperty(property: IdeaProperty, names: NameResolver): IdeaProperty | undefined {
    const relationship = property.relationship
    if (!relationship || !relationship.reverse) return undefined
    return names.property(relationship.idea, relationship.reverse)
}

/**
 * Cardinalidade e exclusividade EFETIVAS de uma propriedade.
 *
 * O espelho não guarda nenhuma das duas: são o inverso do outro lado. Vários valores é
 * o caso comum — um valor só quando o outro lado exige exclusividade, porque aí cada
 * item de lá aponta para um só daqui.
 *
 * Mora aqui, e não em quem desenha, porque a ficha da coleção e o diagrama de classes
 * precisam da mesma resposta.
 */
export function effectiveCardinality(
    property: IdeaProperty, names: NameResolver = ALIAS_ONLY
): { multiple: boolean; unique: any } {
    const mirrored = mirroredProperty(property, names)
    return mirrored ? mirroredCardinality(mirrored)
        : {multiple: !!property.multiple, unique: property.unique}
}

/**
 * A cardinalidade de um ESPELHO, derivada do campo do outro lado — o inverso dele, nas duas
 * pontas: vários valores quando o de lá não é exclusivo, e exclusivo quando o de lá não é
 * múltiplo.
 *
 * Exportada porque o formulário de criar relacionamento reverso diz a mesma coisa em três
 * lugares — as duas caixas somente-leitura e o nome sugerido, que sai no plural justamente
 * quando o espelho é múltiplo. Eram três escritas da mesma fórmula, e a quarta quase nasceu no
 * Kanban.
 */
export function mirroredCardinality(other: IdeaProperty): { multiple: boolean; unique: any } {
    return {multiple: !other.unique, unique: !other.multiple}
}

/**
 * A definição inteira de uma propriedade, na forma de cada conteúdo (ver `PropertyFace`) — a lista do
 * design da coleção. Tudo o que o card de antes dizia, e nada a mais.
 *
 * O identificador não vira selo: na lista, a caixa do identificador já o diz.
 */
export function propertyFace(property: IdeaProperty, names: NameResolver = ALIAS_ONLY): PropertyFace {
    const relationship = property.relationship
    const {multiple, unique} = effectiveCardinality(property, names)
    const mirrored = mirroredProperty(property, names)

    //O que o campo é. Num relacionamento, para onde aponta — ou, no espelho, o campo do outro lado;
    //num atributo, o tipo, e na seleção com qual opção começa
    const what = relationship && relationship.reverse
        ? `${t('definition.mirrors')} ${names.idea(relationship.idea)} · ${mirrored ? mirrored.name : relationship.reverse}`
        : relationship ? `${t('definition.target')} ${names.idea(relationship.idea)}` : typeLabel(property, names)
    const byDefault = defaultLabel(property, names)
    //As opções à parte, e não no texto: são elas que fazem reconhecer o atributo sem abrir o lápis, e
    //se desenham com a cor de cada uma, como o valor no item (pedido do usuário)
    const domain = property.type === 'choice' ? names.domain(property.domain) : undefined

    const alias = formulaName(property.alias)
    return {
        icon: typeIcon(property),
        name: property.name,
        alias: property.alias,
        formulaName: alias === property.alias ? '' : alias,
        description: [what, byDefault && t('definition.starts-with', {value: byDefault}), cardinalityValue(property, multiple)]
            .filter(part => !!part).join(' · '),
        options: domain ? domain.options : [],
        //A cor e o ícone de cada uma vêm do `MARKS`
        marks: [
            markOf('required', t('required'), property.required),
            markOf('unique', t('unique'), unique),
            markOf('computed', t('computed'), !!property.formula),
        ].filter((mark): mark is PropertyMark => !!mark),
        //A fórmula como foi escrita: o que ela lê está nela mesma
        formula: property.formula || '',
        rules: (property.rules || []).map(({rule, message}) => ({rule, message: message || t('validation.rule-default')})),
        //Quem vê e quem edita, como as regras: a fórmula, com a pergunta ao passar o mouse
        access: PROPERTY_ACCESS_KINDS
            .filter(kind => property.access && property.access[kind] !== undefined)
            .map(kind => ({kind, formula: property.access![kind]!, label: t(ACCESS_LABELS[kind])}))
    }
}

/* ------------------------------------------------------------------ *
 * Notação UML — a mesma definição, na forma compacta de uma linha de
 * caixa de classe. Mora aqui, e não num módulo paralelo, porque é este
 * o lugar que sabe o que uma propriedade é.
 * ------------------------------------------------------------------ */

/** Marca tri-estado: ausente, sempre, ou só sob condição. */
export type Marker = {
    /** A condição escrita, quando a marca vale só às vezes. Vazio = vale sempre. */
    condition: string
}

export type PropertySignature = {
    name: string
    alias: string
    /** Rótulo do tipo, ou o nome da coleção de destino num relacionamento */
    typeLabel: string
    icon: string
    multiplicity: string
    relationship: boolean
    identifier: boolean
    required: Marker | null
    unique: Marker | null
    computed: boolean
    /** O valor default escrito, que na UML vem depois da multiplicidade com um "=". */
    defaultValue: string
}

/** `true` vira marca sem condição; um objeto de condição vira a condição escrita. */
function marker(value: any): Marker | null {
    if (!value) return null
    return {condition: value === true ? '' : writeConditions(value)}
}

/**
 * Multiplicidade em notação UML.
 *
 * Um valor só é `1` ou `0..1` conforme a obrigatoriedade; vários valores usam o
 * itemCount quando houver, e `*` como limite superior aberto. O mínimo nunca é
 * menor que 1 numa propriedade obrigatória, mesmo que o itemCount diga 0.
 */
export function multiplicityOf(property: IdeaProperty, names: NameResolver = ALIAS_ONLY): string {
    const {multiple} = effectiveCardinality(property, names)
    const mandatory = !!property.required
    if (!multiple) return mandatory ? '1' : '0..1'
    const count = property.itemCount
    const min = (count && count.min) || (mandatory ? 1 : 0)
    const max = (count && count.max) || 0
    return `${min}..${max || '*'}`
}

/**
 * Multiplicidade do lado de CÁ de uma associação: quantos itens desta coleção podem
 * apontar para o mesmo item da outra. Sai da exclusividade — se cada valor só pode
 * ser usado uma vez, é no máximo um; senão, vários.
 *
 * Não dá para saber se é obrigatório deste lado: isso não é gravado em lugar nenhum,
 * então o mínimo é sempre zero.
 */
export function inverseMultiplicityOf(property: IdeaProperty, names: NameResolver = ALIAS_ONLY): string {
    const {unique} = effectiveCardinality(property, names)
    return unique ? '0..1' : '0..*'
}

/** Uma linha do compartimento de uma caixa de classe. */
export function propertySignature(
    property: IdeaProperty, names: NameResolver = ALIAS_ONLY
): PropertySignature {
    const relationship = property.relationship
    return {
        name: property.name,
        alias: property.alias,
        typeLabel: relationship ? names.idea(relationship.idea) : typeLabel(property, names),
        icon: typeIcon(property),
        multiplicity: multiplicityOf(property, names),
        relationship: !!relationship,
        identifier: !!property.identifier,
        required: marker(property.required),
        unique: marker(effectiveCardinality(property, names).unique),
        computed: !!property.formula,
        defaultValue: defaultLabel(property, names)
    }
}
